import React, { useState } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useMutation } from 'urql';
import { useAppDispatch } from 'hooks/redux';

import useBreakpoint from 'hooks/useBreakpoint';
import Button, { ButtonGroup } from 'components/Button';
import { HeaderNotification } from 'components/TabbedHeaderLayout';
import routeMap from 'config/routes';
import { dismissMutation, followMutation } from 'routes/Inbox/Subscriptions/graphql';
import { showAlert } from 'redux/actions/ui';
import { SUBSCRIPTION_STATES } from 'config/constants';
import { GetLabelAndViewerQuery } from 'gql/graphql';

interface LabelHeaderNotificationProps {
  label: NonNullable<GetLabelAndViewerQuery['label']>;
  isLabelManager: boolean;
  hasReleasesOnLabel: boolean;
  labelSubscriptions: NonNullable<
    NonNullable<NonNullable<GetLabelAndViewerQuery['viewer']>['promoPool']>['labels']
  >['edges'];
}

const LabelHeaderNotification = ({
  label,
  isLabelManager,
  hasReleasesOnLabel,
  labelSubscriptions
}: LabelHeaderNotificationProps) => {
  const dispatch = useAppDispatch();
  const [, acceptPromoInvite] = useMutation(followMutation);
  const [, dismissPromoInvite] = useMutation(dismissMutation);

  const activePromoInvite = labelSubscriptions.find(
    ({ state }) => state === SUBSCRIPTION_STATES.NEW_INVITES
  );

  const [isDismissed, setIsDismissed] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const { pathname } = useLocation();

  const feedbackRouteMatch = matchPath(pathname, {
    path: `${routeMap.labels.root}${routeMap.labels.feedback}`
  });

  const isMobile = useBreakpoint(
    useBreakpoint.BREAKPOINTS.MEDIUM,
    useBreakpoint.DIRECTIONS.DOWN
  );

  if (feedbackRouteMatch && !isLabelManager && hasReleasesOnLabel) {
    return (
      <HeaderNotification>
        <div>
          Displaying feedback received for your artist(s) releases on {label.name}.
        </div>
      </HeaderNotification>
    );
  }

  if (isAccepted) {
    return (
      <HeaderNotification>
        <div>
          Invite accepted! Go to <Link to="/inbox/promos">your Inbox</Link> to start
          downloading your promos.
        </div>
      </HeaderNotification>
    );
  }

  if (isDismissed) {
    return (
      <HeaderNotification>
        <div>
          Invite dismissed. Change your mind? Accept the invite in{' '}
          <Link to="/inbox/subscriptions">your Inbox</Link>.
        </div>
      </HeaderNotification>
    );
  }

  const labelName = label.name || '';

  if (activePromoInvite) {
    return (
      <HeaderNotification>
        <div>
          <div>{`${labelName} has invited you to their promo pool!`}</div>
        </div>
        <ButtonGroup
          className={isMobile ? 'mb-0 mt-2' : 'mb-0 mt-0 ml-3'}
          expand={isMobile}
        >
          <Button
            color={Button.COLORS.PRIMARY}
            onClick={() =>
              acceptPromoInvite({ labelId: label.id }).then(result => {
                if (result.error) {
                  dispatch(showAlert(result.error));
                } else {
                  setIsAccepted(true);
                }
              })
            }
            expand
            data-testid={LabelHeaderNotification.TEST_IDS.ACCEPT}
          >
            Accept
          </Button>

          <Button
            color={Button.COLORS.NEUTRAL}
            onClick={() =>
              dismissPromoInvite({ labelId: label.id }).then(result => {
                if (result.error) {
                  dispatch(showAlert(result.error));
                } else {
                  setIsDismissed(true);
                }
              })
            }
            expand
            data-testid={LabelHeaderNotification.TEST_IDS.DISMISS}
          >
            Dismiss
          </Button>
        </ButtonGroup>
      </HeaderNotification>
    );
  }

  return null;
};

LabelHeaderNotification.TEST_IDS = {
  CONTAINER: HeaderNotification.TEST_IDS.CONTAINER,
  DISMISS: 'HeaderNotification-dismiss',
  ACCEPT: 'HeaderNotification-accept'
};

export default LabelHeaderNotification;
